import React from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, ml, mt, onDesktop, position, underline, w100} from "@pg-design/helpers-css";
import {BrandMapPinIcon, PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {DetailViewHeader} from "../../../atoms/DetailViewHeader";
import {IBoosterClickHandlerParams} from "../../../boosters/types/IBoosterClickHandlerParams";
import {HeaderCta} from "../../../real_estate/components/HeaderCta";
import {Country} from "../../../region/types/Country";
import {IOfferDetailPromotion} from "../../detail/actions/fetch_offer_detail_promotions";
import {OfferHighlightedPrice} from "../../detail/components/OfferHighlightedPrice";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {DetailHeaderDropdownMenu} from "../DetailHeaderDropdownMenu";
import {OfferDetailHeaderPriceButtonVariantBCD} from "./OfferDetailHeaderPriceButtonVariantBCD";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
}

export const OfferDetailHeader = (props: IProps) => {
    const {offer} = props;
    const {vendor} = props.offer;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isForSale = isOfferForSale(offer);
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isPreSale = !!offer.configuration.pre_sale;
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const isArchived = isOfferArchived(offer);

    const isPriceButtonAvailable = isOfferForSale(offer);

    return (
        <div css={wrapper}>
            <DetailViewHeader
                title={
                    <div css={title}>
                        {offer.name} {props.offer.region.country === Country.SPAIN && ` - ${getOfferTypeNamePlural(props.offer.type as OfferType)} Hiszpania`}
                        <br />
                        <OfferHighlightedPrice offer={offer} />
                        <Text as="span" variant="info_txt_1" css={linkStyle}>
                            {offer.investor ? (
                                `Inwestor: ${offer.investor}`
                            ) : (
                                <Link underline href={vendorLink} size="inherit" color="var(--colors-gray-700)">
                                    {" "}
                                    {vendor.name}
                                </Link>
                            )}
                            {` - ${isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type)} na sprzedaż`}
                        </Text>
                    </div>
                }
                cta={
                    <>
                        <HeaderCta>
                            {!isMobile && isPriceButtonAvailable && <OfferDetailHeaderPriceButtonVariantBCD offer={offer} />}
                            <DetailHeaderDropdownMenu css={dropdownMenuADStyle} />
                        </HeaderCta>
                    </>
                }
                badges={
                    (isArchived || isPreSale || isPlanned) && (
                        <>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </>
                    )
                }
                slogan={isForSale && offer.slogan ? offer.slogan : undefined}
                address={offer.address}
                onLocationClick={props.onLocationClick}
                addressRowButton={
                    hasStages && (
                        <Button
                            css={brandButtonStyle}
                            iconLeft={() => <BrandMapPinIcon wrapperColor={theme.colors.primary} size="2.4" wrapperSize="2.4" />}
                            variant="brand_icon"
                            size={isMobile ? "medium" : "big"}
                            onClick={props.onLocationClick}
                        >
                            <Text variant="body_copy_1" css={brandButtonTextStyle}>
                                Zobacz na mapie
                            </Text>
                        </Button>
                    )
                }
                isSpain={props.offer.region.country === Country.SPAIN}
                offerType={props.offer.type}
            />
        </div>
    );
};

const title = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: ${calculateRemSize(1)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${w100};
    ${position("relative")};

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;

const brandButtonStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        padding: 0;
        height: initial;
    }
`;

const brandButtonTextStyle = css`
    text-transform: none;
    ${underline};
    ${ml(0.5)};
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    display: inline-block;
`;

const dropdownMenuADStyle = css`
    ${onDesktop(css`
        ${position("relative")};
        align-self: flex-start;
        ${mt(1)};
    `)}
`;
import React from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {calculateRemSize, display, ml, mt, onDesktop, position, underline, w100} from "@pg-design/helpers-css";
import {BrandMapPinIcon, PercentIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {DetailViewHeader} from "../../../atoms/DetailViewHeader";
import {IBoosterClickHandlerParams} from "../../../boosters/types/IBoosterClickHandlerParams";
import {HeaderCta} from "../../../real_estate/components/HeaderCta";
import {Country} from "../../../region/types/Country";
import {IOfferDetailPromotion} from "../../detail/actions/fetch_offer_detail_promotions";
import {OfferHighlightedPrice} from "../../detail/components/OfferHighlightedPrice";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {isOfferArchived} from "../../utils/is_offer_archived";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {DetailHeaderDropdownMenu} from "../DetailHeaderDropdownMenu";
import {OfferDetailHeaderPriceButtonVariantBCD} from "./OfferDetailHeaderPriceButtonVariantBCD";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
}

export const OfferDetailHeader = (props: IProps) => {
    const {offer} = props;
    const {vendor} = props.offer;

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isForSale = isOfferForSale(offer);
    const isPlanned = offer.configuration.display_type === OfferDisplayType.PLANNED;
    const isPreSale = !!offer.configuration.pre_sale;
    const isInvestmentApartment = offer.slogan && offer.slogan === "Apartamenty inwestycyjne";
    const isArchived = isOfferArchived(offer);

    const isPriceButtonAvailable = isOfferForSale(offer);

    return (
        <div css={wrapper}>
            <DetailViewHeader
                title={
                    <div css={title}>
                        {offer.name} {props.offer.region.country === Country.SPAIN && ` - ${getOfferTypeNamePlural(props.offer.type as OfferType)} Hiszpania`}
                        <br />
                        <OfferHighlightedPrice offer={offer} />
                        <Text as="span" variant="info_txt_1" css={linkStyle}>
                            {offer.investor ? (
                                `Inwestor: ${offer.investor}`
                            ) : (
                                <Link underline href={vendorLink} size="inherit" color="var(--colors-gray-700)">
                                    {" "}
                                    {vendor.name}
                                </Link>
                            )}
                            {` - ${isApartment || isInvestmentApartment ? "apartamenty" : getOfferTypeNamePlural(offer.type)} na sprzedaż`}
                        </Text>
                    </div>
                }
                cta={
                    <>
                        <HeaderCta>
                            {!isMobile && isPriceButtonAvailable && <OfferDetailHeaderPriceButtonVariantBCD offer={offer} />}
                            <DetailHeaderDropdownMenu css={dropdownMenuADStyle} />
                        </HeaderCta>
                    </>
                }
                badges={
                    (isArchived || isPreSale || isPlanned) && (
                        <>
                            {isArchived && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                            {isPreSale && (
                                <Badge variant="label_success">
                                    <strong>Przedsprzedaż</strong>
                                </Badge>
                            )}

                            {isPlanned && (
                                <Badge variant="label_success" icon={PercentIcon}>
                                    Inwestycja planowana
                                </Badge>
                            )}
                        </>
                    )
                }
                slogan={isForSale && offer.slogan ? offer.slogan : undefined}
                address={offer.address}
                onLocationClick={props.onLocationClick}
                addressRowButton={
                    hasStages && (
                        <Button
                            css={brandButtonStyle}
                            iconLeft={() => <BrandMapPinIcon wrapperColor={theme.colors.primary} size="2.4" wrapperSize="2.4" />}
                            variant="brand_icon"
                            size={isMobile ? "medium" : "big"}
                            onClick={props.onLocationClick}
                        >
                            <Text variant="body_copy_1" css={brandButtonTextStyle}>
                                Zobacz na mapie
                            </Text>
                        </Button>
                    )
                }
                isSpain={props.offer.region.country === Country.SPAIN}
                offerType={props.offer.type}
            />
        </div>
    );
};

const title = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: ${calculateRemSize(1)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${w100};
    ${position("relative")};

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;

const brandButtonStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        display: block;
        padding: 0;
        height: initial;
    }
`;

const brandButtonTextStyle = css`
    text-transform: none;
    ${underline};
    ${ml(0.5)};
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors.gray["700"]};
    display: inline-block;
`;

const dropdownMenuADStyle = css`
    ${onDesktop(css`
        ${position("relative")};
        align-self: flex-start;
        ${mt(1)};
    `)}
`;
